import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { selectSkillName, selectQuestions, selectQuestionsLoading, saveQuizAnswer, selectTargetRole, selectMcqAnswers, setMcqAnswer, selectIsRoleFlow } from '../../redux/skillSlice'
import { selectUserName } from '../../redux/nameSlice'
import { toast } from 'react-toastify'
import ProgressBar from '../../components/ProgressBar'
import { selectInitSessionUserDetails } from '../../redux/onboardingUserDetails'
import { capitalise } from '../../utilities/constants'
import { Mixpanel } from '../../config/Mixpanel'
import constants from '../../utilities/constants'
import { selectIsAnalysisPageVisited, selectIsPricingPageVisited } from '../../redux/newAnalysisSlice'

const MCQ = () => {
  const navigate = useNavigate()
  const { questionNumber } = useParams()
  const dispatch = useDispatch()

  // Get data from Redux
  const skillName = useSelector(selectSkillName) || 'Product Management'
  const userName = useSelector(selectUserName)
  const firstName = userName?.split(" ")?.[0] || 'User'
  const questionsFromRedux = useSelector(selectQuestions)
  const questionsLoading = useSelector(selectQuestionsLoading)
  const userSessionDetails = useSelector(selectInitSessionUserDetails)
  const targetRole = useSelector(selectTargetRole)
  const mcqAnswersFromRedux = useSelector(selectMcqAnswers)
  const isRoleFlow = useSelector(selectIsRoleFlow);
  console.log('MCQ - Questions from Redux:', questionsFromRedux)
  console.log('MCQ - Questions Loading:', questionsLoading)
  console.log('MCQ - MCQ Answers from Redux:', mcqAnswersFromRedux)

  // MCQ Questions - Random Product Management questions
  const [selectedQuestions, setSelectedQuestions] = useState([])
  const [selectedAnswers, setSelectedAnswers] = useState({})
  const [showResults, setShowResults] = useState(false)
  const [timeLeft, setTimeLeft] = useState(300) // 5 minutes
  const [isTimerActive, setIsTimerActive] = useState(true)

  const isAnalysisPageVisited = useSelector(selectIsAnalysisPageVisited);
  const isPricingPageVisited = useSelector(selectIsPricingPageVisited)

  // Initialize MCQ answers from Redux when component mounts
  // Comment out to ensure no options are pre-selected by default
  // useEffect(() => {
  //   if (mcqAnswersFromRedux && typeof mcqAnswersFromRedux === 'object' && Object.keys(mcqAnswersFromRedux).length > 0) {
  //     setSelectedAnswers(mcqAnswersFromRedux)
  //     console.log('MCQ - Initialized answers from Redux:', mcqAnswersFromRedux)
  //   }
  // }, [mcqAnswersFromRedux])

  // Get current question number from URL (default to 1)
  const currentQuestion = questionNumber ? parseInt(questionNumber) - 1 : 0
  const totalQuestions = 5


  useEffect(() => {
    if(isAnalysisPageVisited){
      navigate('/onboarding/analysis')
    }

    else if(isPricingPageVisited){
      navigate('/onboarding/pricing')
    }
    
  }, [dispatch, isAnalysisPageVisited, isPricingPageVisited, navigate])

  // Initialize questions and redirect if no question number
  useEffect(() => {
    // Track MCQ page load
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_MCQ_PAGE_LOADED, {
      skill: skillName,
      questionNumber: currentQuestion + 1,
      totalQuestions: totalQuestions,
      targetRole: targetRole,
      session_id: userSessionDetails?.session_id
    })

    // If accessed via /onboarding/4, redirect to first question
    if (!questionNumber && window.location.pathname === '/onboarding/4') {
      navigate('/onboarding/mcq/1', { replace: true })
      return
    }

    // If no question number in mcq route, redirect to question 1
    if (!questionNumber) {
      navigate('/onboarding/mcq/1', { replace: true })
      return
    }

    // Validate question number
    const qNum = parseInt(questionNumber)
    if (qNum < 1 || qNum > totalQuestions) {
      navigate('/onboarding/mcq/1', { replace: true })
      return
    }

    // Use questions from Redux if available, otherwise use fallback questions
    const questionsToUse = questionsFromRedux && questionsFromRedux.length > 0 ? questionsFromRedux : getFallbackQuestions()

    // Select random questions or use provided questions
    if (selectedQuestions.length === 0) {
      const randomQuestions = questionsToUse.slice(0, 5) // Take first 5 questions
      setSelectedQuestions(randomQuestions)
      console.log('MCQ - Using questions:', randomQuestions)
    }

  }, [questionNumber, navigate, selectedQuestions.length, questionsFromRedux, skillName, currentQuestion, totalQuestions, targetRole, userSessionDetails?.session_id])

  const getFallbackQuestions = () => {
    const mcqQuestions = [
      {
        id: 1,
        question: "What is the primary purpose of a Product Requirements Document (PRD)?",
        options: [
          "To define technical architecture",
          "To outline product features and requirements",
          "To track development progress",
          "To manage team resources"
        ],
        correctAnswer: 1,
        explanation: "A PRD outlines what needs to be built and why, serving as a blueprint for the product."
      },
      {
        id: 2,
        question: "Which metric is most important for measuring user engagement?",
        options: [
          "Number of downloads",
          "Daily Active Users (DAU)",
          "Revenue per user",
          "App store ratings"
        ],
        correctAnswer: 1,
        explanation: "DAU measures how many users actively engage with your product daily, indicating true engagement."
      },
      {
        id: 3,
        question: "What does 'MVP' stand for in product development?",
        options: [
          "Most Valuable Player",
          "Minimum Viable Product",
          "Maximum Value Proposition",
          "Major Version Production"
        ],
        correctAnswer: 1,
        explanation: "MVP is a product with minimum features to satisfy early customers and provide feedback for development."
      },
      {
        id: 4,
        question: "Which framework helps prioritize features based on impact and effort?",
        options: [
          "Scrum Framework",
          "Design Thinking",
          "Impact vs Effort Matrix",
          "Waterfall Method"
        ],
        correctAnswer: 2,
        explanation: "Impact vs Effort Matrix helps prioritize features by plotting their potential impact against implementation effort."
      },
      {
        id: 5,
        question: "What is the main goal of user persona development?",
        options: [
          "To increase development speed",
          "To understand target user needs and behaviors",
          "To reduce product costs",
          "To improve code quality"
        ],
        correctAnswer: 1,
        explanation: "User personas help teams understand and empathize with target users to build better products."
      }
    ]

    return mcqQuestions
  }

  const handleSubmitTest = (answers = selectedAnswers) => {
    setIsTimerActive(false)
    setShowResults(true)

    // Calculate score
    let correctCount = 0
    selectedQuestions.forEach((question, index) => {
      if (answers[index] === question.correctAnswer) {
        correctCount++
      }
    })

    const percentage = (correctCount / selectedQuestions.length) * 100
    toast.success(`Test completed! You scored ${percentage.toFixed(0)}%`)

    // Navigate to user details step after 3 seconds
    setTimeout(() => {
      navigate('/onboarding/userdetails')
    }, 3000)
  }


  const handleAnswerSelect = (answerIndex) => {
    // Track MCQ answer
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_MCQ_QUESTION_ANSWERED, {
      skill: skillName,
      questionNumber: currentQuestion + 1,
      selectedAnswerIndex: answerIndex,
      session_id: userSessionDetails?.session_id
    })

    // Save the answer locally
    const updatedAnswers = {
      ...selectedAnswers,
      [currentQuestion]: answerIndex
    }
    setSelectedAnswers(updatedAnswers)
    
    // Save the answer to Redux
    dispatch(setMcqAnswer({
      questionIndex: currentQuestion,
      answerIndex: answerIndex
    }))

    // Auto-advance to next question after a brief delay
    setTimeout(async () => {
      const currentQForApi = selectedQuestions?.[currentQuestion]
      const selectedOptionText = currentQForApi?.options?.[answerIndex]

      if (!userSessionDetails?.session_id) {
        toast.error('Session not found. Please refresh and try again.')
        return
      }

      if (!currentQForApi?.question || !selectedOptionText) {
        toast.error('Unable to save your answer. Please try again.')
        return
      }

      try {
        await dispatch(saveQuizAnswer({
          session_id: userSessionDetails.session_id,
          question: currentQForApi.question,
          selected_option: selectedOptionText
        })).unwrap()
      } catch (e) {
        toast.error('Failed to save answer. Please try again.')
        return
      }

      if (currentQuestion < totalQuestions - 1) {
        // Navigate to next question
        navigate(`/onboarding/mcq/${currentQuestion + 2}`)
      } else {
        // Track MCQ completion
        Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_MCQ_COMPLETED, {
          skill: skillName,
          totalQuestions: totalQuestions,
          session_id: userSessionDetails?.session_id
        })
        
        // This was the last question, submit the test
        navigate('/onboarding/userdetails')
      }
    }, 500) // Small delay to show the selection
  }

  if (selectedQuestions.length === 0) {
    return (
      <div className='mt-6 w-full md:max-w-[45rem] flex-1 mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative flex flex-col items-center justify-center'>
        <div className='animate-pulse text-[#83BEFF] text-lg'>Loading questions...</div>
      </div>
    )
  }

  // Check if current question is valid
  if (currentQuestion >= selectedQuestions.length || currentQuestion < 0) {
    return (
      <div className='mt-6 w-full md:max-w-[45rem] flex-1 mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative flex flex-col items-center justify-center'>
        <div className='text-[#83BEFF] text-lg'>Invalid question number</div>
      </div>
    )
  }

  const currentQ = selectedQuestions[currentQuestion]

  // Show loading state while questions are being fetched
  if (questionsLoading && selectedQuestions.length === 0) {
    return (
      <div className='mt-6 w-full md:max-w-[45rem] flex-1 mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative flex flex-col items-center justify-center'>
        <div className='animate-pulse text-[#83BEFF] text-lg mb-4'>Loading questions...</div>
        <div className="w-8 h-8 border-2 border-[#83BEFF] border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }

  // Show message if no questions available
  if (!currentQ) {
    return (
      <div className='mt-6 w-full md:max-w-[45rem] flex-1 mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative flex flex-col items-center justify-center'>
        <div className='text-[#83BEFF] text-lg'>No questions available</div>
      </div>
    )
  }

  return (
    <div className='mt-8 w-full md:max-w-[45rem] flex-1 mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative flex flex-col justify-between items-center flex-1 gap-6'>
      {/* Progress bar and step indicator */}
      <div className='w-full flex flex-col justify-start items-center'>
        <ProgressBar
          currentStep={isRoleFlow ? 6 : 4}
          totalSteps={isRoleFlow ? 8 : 6}
          stepText={`Step ${isRoleFlow ? 6 : 4}/${isRoleFlow ? 8 : 6} to  ${targetRole ? 'becoming ' + targetRole : 'mastering ' + capitalise(skillName)}`}
        />

        {/* Main content card */}
        <div className='w-full mt-4 flex flex-col p-[2px] rounded-[1.5rem]' style={{
          backgroundImage: `linear-gradient(
          to right,
          rgba(64, 106, 255, 0.3) 0%,
          rgba(64, 106, 255, 1) 56%,
          rgba(64, 106, 255, 0.3) 100%
        )`
        }}>
          <div className='flex flex-col p-6 rounded-[1.5rem]  shadow-[0px_0px_10px_0px_rgba(0,0,0,0.5)] relative overflow-hidden' style={{ backgroundImage: `radial-gradient(circle, #0B305F 0%, #0A1E3F 100%)` }}>
          <div className="absolute inset-0 opacity-40 rounded-3xl bg-repeat bg-[length:180px_180px]"
              style={{ backgroundImage: `url('https://assets.learntube.ai/files/personalise/web.webp')` }} />


            {/* Step Progress Bar */}
            <div className='flex items-center justify-center mb-6 relative z-10'>
              <div className='flex-1 flex items-center gap-2'>
                {Array.from({ length: totalQuestions }, (_, index) => (
                  <React.Fragment key={index}>
                    {/* Progress segment */}
                    <div className='flex-1 items-center'>
                      <div
                        className={`h-1 flex-1 rounded-full transition-all duration-300 ${index < currentQuestion + 1
                            ? 'bg-[#406AFF]'
                            : 'bg-[#406AFF]/30'
                          }`}
                      />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
            {/* Content */}
            <div className='flex flex-col gap-8 relative z-10'>
              {/* Question and options */}
              <div className='flex flex-col gap-6 w-full'>
                {/* Question */}
                <div className='flex flex-col w-full'>
                  <div className='flex flex-col font-normal justify-center text-[20px] text-white w-full leading-[1.2]'>
                    <p>{currentQ.question}</p>
                  </div>
                </div>

                {/* Options */}
                <div className='flex flex-col gap-3 w-full'>
                  {currentQ.options.map((option, index) => {
                    // Ensure we only check for selection on the current question
                    const isSelected = selectedAnswers[currentQuestion] === index

                    return (
                      <div
                        key={`q${currentQuestion}-option${index}`}
                        className={`${isSelected
                            ? 'bg-[#7FC241]/20 border-[#7FC241] shadow-[0px_0px_2px_0px_#7fc241]'
                            : 'bg-[#406AFF]/15 border-[#406AFF]/70 hover:bg-[#406AFF]/25 hover:border-[#406AFF]'
                          } border border-solid flex gap-2 items-center p-4 rounded-lg cursor-pointer transition-all duration-200 w-full group`}
                        onClick={() => handleAnswerSelect(index)}
                      >
                        {/* Radio button */}
                        <div className='relative w-[14px] h-[14px] shrink-0'>
                          <div className={`w-[14px] h-[14px] rounded-full border-2 ${isSelected 
                              ? 'border-[#7FC241]' 
                              : 'border-[#70AEFF] '
                            }`}>
                            <div className={`w-[6px] h-[6px] rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all ${isSelected 
                                ? 'bg-[#7FC241] opacity-100' 
                                : 'bg-[#70AEFF] opacity-0'
                              }`}></div>
                          </div>
                        </div>

                        {/* Option text */}
                        <div className='flex flex-col font-normal justify-center text-[14px] text-white'>
                          <p>{option}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom statistic */}
      <div className='flex flex-col items-center justify-center w-full'>
        <div className='flex flex-col items-center justify-center w-full'>
          <p className='font-medium italic text-[#83BEFF] text-[12px] text-center w-full leading-[1.4]'>
            📈 63% of learners landed interviews within 30 days of completing personalised course
          </p>
        </div>
      </div>
    </div>
  )
}

export default MCQ